import { IPolicyLookupResponse } from './interfaces/IPolicyLookupResponse';
import { Component, HostListener, OnInit, OnDestroy, Inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { UiBlockService } from './core/ui-block.service';
import { Store } from '@ngrx/store';
import { VIEW_PERMISSIONS } from './store/actions/user-preferences-action';
import { AppState } from './store/app.state';
import { UserPreferenceResponse } from './store/model/user-preferences.interface';
import { VIEW_POLICY_INFO } from './store/actions/policy-lookup-action';
import { IAppConfig } from './models/config';
import { AppService } from './app.service';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import { Observable, Subject } from 'rxjs';
import { OktaUserInfoService } from './core/okta-user-info.service';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public isUiBlocked = false;
  displayNav = false;
  appConfig: IAppConfig = {} as IAppConfig;
  userApiUrl = '';
  lookupApiUrl = '';
  displayContent = false;
  userResponse: any = null;
  isDisclaimerLoaded = false;
  isSuperUser = false;
  isImpersonateMode = false;

  public isAuthenticated$!: Observable<boolean>;
  public name$!: Observable<string>;
  public isSidebarVisible = window.innerWidth > 1320;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly appService: AppService,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly authStateService: OktaAuthStateService,
    private readonly oktaUserInfoService: OktaUserInfoService,
    private readonly uiBlockService: UiBlockService
  ) {
    this.uiBlockService.block$.subscribe(blocked => {
      this.isUiBlocked = blocked;
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.oktaAuth.session.get().then(user => {
          if (user.status !== 'ACTIVE')
            this.oktaAuth.signOut();
        },
          error => {
            this.oktaAuth.signOut();
          });
      }
    });
  }

  ngOnInit(): void {
    this.appConfig = this.appService.readConfig() || ({} as any);

    // Track page views for analytics
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      console.log('[AppComponent] Navigation to:', event.url);

      // Hide auth splash on public routes (callback / logout) where no auth is required
      if (this.isPublicRoute(event.url)) {
        this.hideAuthSplash();
      }
    });

    // ✅ Only fetch user info when authenticated
    this.authStateService.authState$.pipe(
      takeUntil(this.destroy$),
      filter((authState: AuthState) => !!authState && authState.isAuthenticated === true)
    ).subscribe({
      next: () => {
        this.hideAuthSplash();
        this.loadUserInfo();
      },
      error: (error) => {
        console.error('[AppComponent] Auth state error:', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isPublicRoute(url: string): boolean {
    return url.startsWith('/auth/callback') || url.startsWith('/logout');
  }

  private hideAuthSplash(): void {
    const splash = document.getElementById('auth-splash');
    if (splash && splash.parentNode) {
      splash.parentNode.removeChild(splash);
    }
  }

  private loadUserInfo(): void {
    this.oktaUserInfoService.getUserInfo().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (userInfo) => {
        if (userInfo && this.appConfig) {
          console.log('[AppComponent] User info loaded successfully');
          this.appConfig.username = userInfo.preferred_username ?? '';
          this.appConfig.userINumber = this.appConfig.username.split('@')[0];
        }
      },
      error: (err) => {
        console.error('[AppComponent] Error loading user info:', err);
      }
    });
  }

  userInfo(resp: UserPreferenceResponse) {
    this.displayNav = true;
    this.store.dispatch({
      type: VIEW_PERMISSIONS,
      payload: resp,
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    const width = (event.target as Window).innerWidth;
    this.isSidebarVisible = width > 1320;
  }

  policyInfo(resp: IPolicyLookupResponse) {
    this.store.dispatch({
      type: VIEW_POLICY_INFO,
      payload: resp,
    });
  }
}